import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t, tKey } from 'ember-intl';
import { eq, not } from 'ember-truth-helpers';
import type { BudgetForecastEntry } from 'lumen/models';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export interface SettingsBudgetForecastModalsModalCreateEntrySignature {
  Args: {
    isTitleUnique: (title: string) => boolean;
    onClose: () => void;
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastModalsModalCreateEntry extends Component<SettingsBudgetForecastModalsModalCreateEntrySignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  /**
   * This is used to conditionally show validation errors.
   */
  @tracked hasSubmitted = false;
  @tracked title = '';

  createEntryTask = task(async (): Promise<void> => {
    this.hasSubmitted = true;

    const isTitleValid = this.title.trim().length > 0 && this.isTitleUnique;

    if (!isTitleValid) {
      return;
    }

    try {
      const newEntry = this.store.createRecord<BudgetForecastEntry>(
        'budget-forecast/entry',
        { title: this.title.trim(), dataType: this.args.view },
      );
      await newEntry.save();

      this.#handleEntryCreatedSuccess(newEntry);
    } catch (error) {
      this.errorTracking.captureException(error, {
        feature: 'budget-forecast.create-entry',
      });
      void this.alerting.showErrorAlert();

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get isTitleUnique(): boolean {
    return this.args.isTitleUnique(this.title.trim());
  }

  #handleEntryCreatedSuccess(newEntry: BudgetForecastEntry): void {
    const routeToTransitionTo =
      newEntry.dataType === 'budget'
        ? 'hotel.settings.budget.detail'
        : 'hotel.settings.forecast.detail';

    this.router.transitionTo(routeToTransitionTo, newEntry.id);

    const toastMessageKey =
      newEntry.dataType === 'budget'
        ? tKey('budget_forecast.budget.created')
        : tKey('budget_forecast.forecast.created');

    this.toast.success(this.intl.t(toastMessageKey), { showIcon: true });
  }

  @action
  updateTitle(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.title = input.value;
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.createEntryTask.isRunning}}
      @onClose={{@onClose}}
      @title={{if
        (eq @view "budget")
        (t "budget_forecast.budget.create_entry")
        (t "budget_forecast.forecast.create_entry")
      }}
      data-test-modal="create-entry"
    >
      <:default>
        <div class="modal--padded">
          <div class="flex flex-col" data-test-field="title">
            <PrismInput
              @errorMessage={{t
                "budget_forecast.create_entry.error.title_not_unique"
              }}
              @hasErrors={{not this.isTitleUnique}}
              @isTouched={{this.hasSubmitted}}
              @label={{t "budget_forecast.title"}}
              @onInput={{this.updateTitle}}
              @placeholder={{t "budget_forecast.add_title"}}
              @required={{true}}
              @requiredErrorMessage={{t "general.required"}}
              @value={{this.title}}
              data-test-input="title"
            />
          </div>
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end px-500">
          <PrismButton
            @isDisabled={{this.createEntryTask.isRunning}}
            @isLoading={{this.createEntryTask.isRunning}}
            @label={{if
              (eq @view "budget")
              (t "budget_forecast.create_budget")
              (t "budget_forecast.create_forecast")
            }}
            @onClick={{this.createEntryTask.perform}}
            data-test-button="create-entry"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
